import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWithAuth } from '../../lib/apiClient';

function encodeIdentifier(value) {
  return encodeURIComponent(String(value || '').trim());
}

export function useAccessScope(enabled = true) {
  return useQuery({
    queryKey: ['access-scope', 'me'],
    queryFn: async () => {
      const result = await fetchWithAuth('/api/access-scope/me');
      return result?.access || null;
    },
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useOrganizationGroups(orgIdOrName, options = {}) {
  const userId = options.userId || null;
  return useQuery({
    queryKey: ['org-groups', orgIdOrName || null, userId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (userId) params.set('userId', userId);
      const query = params.toString();
      return fetchWithAuth(`/api/org-groups/${encodeIdentifier(orgIdOrName)}${query ? `?${query}` : ''}`);
    },
    enabled: options.enabled !== false && !!orgIdOrName,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useOrganizationGroupsMap(orgIdentifiers = [], enabled = true) {
  const normalized = Array.from(new Set((orgIdentifiers || []).map((value) => String(value || '').trim()).filter(Boolean))).sort();
  return useQuery({
    queryKey: ['org-groups', 'map', normalized],
    queryFn: async () => {
      const results = await Promise.all(
        normalized.map(async (identifier) => {
          const payload = await fetchWithAuth(`/api/org-groups/${encodeIdentifier(identifier)}`);
          return [identifier, payload];
        })
      );
      return Object.fromEntries(results);
    },
    enabled: enabled && normalized.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useCreateOrgGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgIdOrName, payload }) => {
      return fetchWithAuth(`/api/org-groups/${encodeIdentifier(orgIdOrName)}`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['org-groups', variables.orgIdOrName] });
      queryClient.invalidateQueries({ queryKey: ['org-groups', 'map'] });
      queryClient.invalidateQueries({ queryKey: ['access-scope', 'me'] });
    },
  });
}

export function useUpdateOrgGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, payload }) => {
      return fetchWithAuth(`/api/org-groups/${encodeIdentifier(groupId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload || {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-groups'] });
      queryClient.invalidateQueries({ queryKey: ['access-scope', 'me'] });
    },
  });
}

export function useDeleteOrgGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (groupId) => {
      return fetchWithAuth(`/api/org-groups/${encodeIdentifier(groupId)}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-groups'] });
      queryClient.invalidateQueries({ queryKey: ['access-scope', 'me'] });
    },
  });
}

export function useUpdateUserGroupMemberships() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, organizationId, groupIds }) => {
      return fetchWithAuth(`/api/org-groups/users/${encodeIdentifier(userId)}/memberships`, {
        method: 'PUT',
        body: JSON.stringify({
          organizationId,
          groupIds: Array.isArray(groupIds) ? groupIds : [],
        }),
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['org-groups'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['access-scope', 'me'] });
      if (variables?.userId) {
        queryClient.invalidateQueries({ queryKey: ['user_access', variables.userId] });
      }
    },
  });
}
